import type { SiteChatLang } from './types';

/**
 * Fixed labels of the «rate the conversation» card, per widget language (see `ui.ts`). The question itself is the
 * owner's text (`SiteChatConfig.csatQuestion`, defaults in `phrases.ts`).
 */
export type SiteChatCsatUi = {
    /** Names of the five faces, from 1 (very bad) to 5 (great): their titles and what screen readers say. */
    csatScores: [string, string, string, string, string];
    /** The comment box after a score of 1–3. */
    csatCommentPlaceholder: string;
    /** After a score of 4–5, and on a closed card that was rated. */
    csatThanks: string;
    /** Once the comment is sent. */
    csatThanksComment: string;
    /** A card nobody rated that takes no answers any more. */
    csatClosed: string;
    /** The answer did not reach us; the card shows the one from before. */
    csatFailed: string;
};

export const CSAT_UI: Record<SiteChatLang, SiteChatCsatUi> = {
    en: {
        csatScores: ['Very bad', 'Bad', 'OK', 'Good', 'Great'],
        csatCommentPlaceholder: 'What could we do better?',
        csatThanks: 'Thanks for your rating!',
        csatThanksComment: 'Thank you, we’ll read your feedback',
        csatClosed: 'Rating is no longer available',
        csatFailed: 'Not sent. Try again',
    },
    ru: {
        csatScores: ['Очень плохо', 'Плохо', 'Нормально', 'Хорошо', 'Отлично'],
        csatCommentPlaceholder: 'Что нам стоит улучшить?',
        csatThanks: 'Спасибо за оценку!',
        csatThanksComment: 'Спасибо, мы прочитаем ваш отзыв',
        csatClosed: 'Оценка больше недоступна',
        csatFailed: 'Не отправилось. Попробуйте ещё раз',
    },
    uk: {
        csatScores: ['Дуже погано', 'Погано', 'Нормально', 'Добре', 'Чудово'],
        csatCommentPlaceholder: 'Що нам варто покращити?',
        csatThanks: 'Дякуємо за оцінку!',
        csatThanksComment: 'Дякуємо, ми прочитаємо ваш відгук',
        csatClosed: 'Оцінка вже недоступна',
        csatFailed: 'Не надіслано. Спробуйте ще раз',
    },
    es: {
        csatScores: ['Muy mal', 'Mal', 'Regular', 'Bien', 'Excelente'],
        csatCommentPlaceholder: '¿Qué podríamos mejorar?',
        csatThanks: '¡Gracias por tu valoración!',
        csatThanksComment: 'Gracias, leeremos tu comentario',
        csatClosed: 'La valoración ya no está disponible',
        csatFailed: 'No se envió. Inténtalo de nuevo',
    },
    id: {
        csatScores: ['Sangat buruk', 'Buruk', 'Biasa saja', 'Baik', 'Luar biasa'],
        csatCommentPlaceholder: 'Apa yang bisa kami perbaiki?',
        csatThanks: 'Terima kasih atas penilaian Anda!',
        csatThanksComment: 'Terima kasih, kami akan membaca masukan Anda',
        csatClosed: 'Penilaian sudah tidak tersedia',
        csatFailed: 'Tidak terkirim. Coba lagi',
    },
    lt: {
        csatScores: ['Labai blogai', 'Blogai', 'Normaliai', 'Gerai', 'Puikiai'],
        csatCommentPlaceholder: 'Ką galėtume patobulinti?',
        csatThanks: 'Ačiū už įvertinimą!',
        csatThanksComment: 'Ačiū, perskaitysime jūsų atsiliepimą',
        csatClosed: 'Įvertinti nebegalima',
        csatFailed: 'Neišsiųsta. Bandykite dar kartą',
    },
    pt: {
        csatScores: ['Muito ruim', 'Ruim', 'Regular', 'Bom', 'Ótimo'],
        csatCommentPlaceholder: 'O que podemos melhorar?',
        csatThanks: 'Obrigado pela avaliação!',
        csatThanksComment: 'Obrigado, vamos ler o seu comentário',
        csatClosed: 'A avaliação não está mais disponível',
        csatFailed: 'Não foi enviado. Tente de novo',
    },
    zhcn: {
        csatScores: ['很差', '较差', '一般', '满意', '非常满意'],
        csatCommentPlaceholder: '我们有哪些地方可以做得更好？',
        csatThanks: '感谢您的评价！',
        csatThanksComment: '谢谢，我们会认真阅读您的反馈',
        csatClosed: '评价已关闭',
        csatFailed: '发送失败，请重试',
    },
    zhtw: {
        csatScores: ['很差', '不太好', '普通', '滿意', '非常滿意'],
        csatCommentPlaceholder: '我們有哪些地方可以做得更好？',
        csatThanks: '感謝您的評價！',
        csatThanksComment: '謝謝，我們會認真閱讀您的意見',
        csatClosed: '評價已關閉',
        csatFailed: '傳送失敗，請重試',
    },
};
